import { useMemo, useCallback, forwardRef } from 'react';
import { Form, Space } from 'antd';
import { UserAddOutlined } from '@ant-design/icons';
import AppModal from '@pages/inside/tenant/AppModal';
import { Button } from '@components/ui/Button';
import { Select as UiSelect } from '@/components/ui/Select';
import { HoverRevealRemoveChip } from '@/components/ui/HoverRevealRemoveChip';
import {
    CreateRoleModalBody,
    CreateRoleModalFooter,
    PillsWrap,
    ListItemTitle,
    ListItemMeta,
    MutedText,
} from '@pages/inside/tenant/style';

/**
 * Single search-select: each pick appends to `userIds`; picked users are hidden from the list.
 * Used inside Form.Item with value = userIds[] and onChange(nextIds).
 */
const InviteMembersPicker = forwardRef(function InviteMembersPicker(
    { value, onChange, inviteSelectOptions, filterSelectOption },
    ref,
) {
    const userIds = Array.isArray(value) ? value : [];

    const optionByValue = useMemo(() => {
        const m = new Map();
        for (const o of inviteSelectOptions || []) {
            m.set(o.value, o);
            m.set(String(o.value), o);
        }
        return m;
    }, [inviteSelectOptions]);

    const selectedRows = useMemo(
        () =>
            userIds
                .map((id) => optionByValue.get(id) ?? optionByValue.get(String(id)))
                .filter(Boolean),
        [userIds, optionByValue],
    );

    const availableOptions = useMemo(() => {
        const sel = new Set(userIds.map((id) => String(id)));
        return (inviteSelectOptions || []).filter((o) => !sel.has(String(o.value)));
    }, [inviteSelectOptions, userIds]);

    const addOne = useCallback(
        (pickedId) => {
            if (pickedId == null || pickedId === '') return;
            if (userIds.some((id) => String(id) === String(pickedId))) return;
            onChange([...userIds, pickedId]);
        },
        [userIds, onChange],
    );

    const removeSelected = useCallback(
        (valueToRemove) => {
            onChange(
                userIds.filter((id) => id !== valueToRemove && String(id) !== String(valueToRemove)),
            );
        },
        [userIds, onChange],
    );

    const renderOption = useCallback((option) => {
        const d = option?.data ?? option ?? {};
        return (
            <div style={{ lineHeight: 1.35 }}>
                <ListItemTitle style={{ marginBottom: 4, fontSize: 13 }}>
                    {d.displayName}
                </ListItemTitle>
                <ListItemMeta style={{ marginBottom: d.email ? 4 : 0 }}>{d.roleLabel}</ListItemMeta>
                {d.email ? (
                    <MutedText $marginBottom="0" style={{ fontSize: 12 }}>
                        {d.email}
                    </MutedText>
                ) : null}
            </div>
        );
    }, []);

    const metaTooltip = useCallback((opt) => {
        const emailLine = opt.email ? String(opt.email).trim() : '';
        const roleLine = opt.roleLabel != null ? String(opt.roleLabel).trim() : '';
        const base = {
            color: '#ffffff',
            fontFamily: 'var(--font-family-geist)',
            wordBreak: 'break-word',
        };
        const emailStyle = {
            ...base,
            fontSize: 14,
            lineHeight: 1.4,
            fontWeight: 600,
            marginBottom: emailLine && roleLine ? 6 : 0,
        };
        const roleStyle = {
            ...base,
            fontSize: 11,
            lineHeight: 1.35,
            fontWeight: 500,
            color: 'rgba(255, 255, 255, 0.82)',
            letterSpacing: '0.02em',
        };
        return (
            <div>
                {emailLine ? <div style={emailStyle}>{emailLine}</div> : null}
                <div style={roleStyle}>{roleLine || '—'}</div>
            </div>
        );
    }, []);

    return (
        <div ref={ref} style={{ width: '100%' }}>
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
                <UiSelect
                    key={userIds.join('\u001f')}
                    $width="100%"
                    showSearch
                    allowClear
                    placeholder="Search and select users to add"
                    onChange={addOne}
                    options={availableOptions}
                    filterOption={filterSelectOption}
                    optionRender={renderOption}
                    popupMatchSelectWidth={false}
                    virtual={false}
                    listHeight={288}
                    styles={{
                        popup: {
                            root: { minWidth: 320 },
                        },
                    }}
                />
                {selectedRows.length > 0 ? (
                    <PillsWrap role="list" aria-label="Selected users" style={{ alignItems: 'flex-start' }}>
                        {selectedRows.map((opt) => (
                            <HoverRevealRemoveChip
                                key={String(opt.value)}
                                role="listitem"
                                label={opt.displayName}
                                tooltipTitle={metaTooltip(opt)}
                                onRemove={() => removeSelected(opt.value)}
                                removeAriaLabel={`Remove ${opt.displayName || 'user'}`}
                            />
                        ))}
                    </PillsWrap>
                ) : null}
            </Space>
        </div>
    );
});
InviteMembersPicker.displayName = 'InviteMembersPicker';

/**
 * Invite existing tenant users as project contributors (shared by Contributors tab and Project details).
 */
export default function InviteMemberModal({
    open,
    onCancel,
    form,
    inviteSelectOptions,
    filterSelectOption,
    addContributorsPending,
    onSubmit,
    onCreateUserClick,
    canCreateUser,
}) {
    return (
        <AppModal
            size="md"
            title="Add Member"
            open={open}
            onCancel={onCancel}
            footer={null}
            classNames={{ header: 'submission-modal__header' }}
        >
            <CreateRoleModalBody>
                <Form form={form} layout="vertical">
                    <Form.Item label="Users" style={{ marginBottom: 0 }}>
                        <Form.Item
                            name="userIds"
                            rules={[
                                {
                                    validator: (_, v) =>
                                        Array.isArray(v) && v.length > 0
                                            ? Promise.resolve()
                                            : Promise.reject(new Error('Select at least one user')),
                                },
                            ]}
                            noStyle
                        >
                            <InviteMembersPicker
                                inviteSelectOptions={inviteSelectOptions}
                                filterSelectOption={filterSelectOption}
                            />
                        </Form.Item>
                    </Form.Item>
                </Form>
            </CreateRoleModalBody>
            <CreateRoleModalFooter
                style={{
                    justifyContent: 'flex-start',
                    flexWrap: 'wrap',
                    gap: 12,
                    width: '100%',
                }}
            >
                {typeof onCreateUserClick === 'function' && (
                    <Button
                        type="button"
                        icon={<UserAddOutlined />}
                        $primary={false}
                        $size="14px"
                        onClick={onCreateUserClick}
                        permissionDenied={!canCreateUser}
                    >
                        Create user
                    </Button>
                )}
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    <Button type="button" $primary={false} $size="14px" onClick={onCancel}>
                        Cancel
                    </Button>
                    <Button type="button" $primary $blackOnLight $size="14px" loading={addContributorsPending} onClick={onSubmit}>
                        Add
                    </Button>
                </div>
            </CreateRoleModalFooter>
        </AppModal>
    );
}
